"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { GestorToggle } from "./GestorToggle";
import { NomeUsuario, NomeUsuarioComPonto } from "./NomeUsuario";
import { ConfigButton } from "./ConfigButton";
import { MenuPerfil } from "./MenuPerfil";
import { FotoPerfilUsuario } from "./AvatarComFoto";
import { buscarJsonComCache } from "@/lib/fetch-cache-cliente";
import { useExperienciaProduto } from "@/components/ExperienciaProvider";

const FERA_BADGES = [
  { meta: 31, src: "/badge-prata.png", alt: "Broche prata Feras da BD" },
  { meta: 41, src: "/badge-ouro.png", alt: "Broche ouro Feras da BD" },
  { meta: 51, src: "/badge-diamante.png", alt: "Broche diamante Feras da BD" },
  { meta: 61, src: "/badge-rubi.png", alt: "Broche rubi Feras da BD" },
];

function getSaudacao() {
  const hora = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Sao_Paulo" })).getHours();
  if (hora >= 6 && hora < 12) return "Bom dia";
  if (hora >= 12 && hora < 18) return "Boa tarde";
  return "Boa noite";
}

export function Header({ variant = "default", integrado = false, followProAnalise = false, followProUsuario = false, analiseGestorFollowZap = false, administrativo = false }: { variant?: "default" | "user-desktop"; integrado?: boolean; followProAnalise?: boolean; followProUsuario?: boolean; analiseGestorFollowZap?: boolean; administrativo?: boolean }) {
  const { dados, experiencia, carregado: experienciaCarregada, experienciaConfirmada } = useExperienciaProduto();
  const [totalVendas, setTotalVendas] = useState(0);
  const areaAnaliseGestor = followProAnalise || analiseGestorFollowZap;
  const semElementosBp = areaAnaliseGestor || followProUsuario || administrativo;
  const mostrarBroches = experienciaConfirmada && experiencia.produto === "FOLLOW_CUSTOM";
  const badgesLiberados = semElementosBp || !mostrarBroches ? [] : FERA_BADGES.filter((badge) => totalVendas >= badge.meta);
  const paddingMobile = process.env.NODE_ENV === "development" ? "pl-4 pr-20" : "px-4";
  const marca = administrativo ? { nome: "Administração", sigla: "PA" } : followProAnalise || followProUsuario ? { nome: "FollowPro", sigla: "FP" } : experienciaCarregada && experienciaConfirmada ? experiencia.marca : null;
  const menuPerfilDisponivel = dados && (dados.papel === "USUARIO" || dados.papel === "GESTOR") &&
    (dados.produto === "FOLLOW_ZAP" || dados.produto === "FOLLOW_PRO");
  const entradaPerfil = !experienciaConfirmada ? null : menuPerfilDisponivel ? <MenuPerfil /> : <ConfigButton />;

  useEffect(() => {
    if (variant !== "user-desktop" || semElementosBp) return;

    buscarJsonComCache<{ totalVendas?: number }>("/api/vendas")
      .then((dados) => setTotalVendas(Number(dados?.totalVendas ?? 0)))
      .catch(() => setTotalVendas(0));
  }, [variant, semElementosBp]);

  const saudacao = useSyncExternalStore(
    () => () => {}, // subscribe: sem evento; a saudação é recalculada a cada render
    () => getSaudacao(), // snapshot do cliente
    () => "", // snapshot do servidor e da hidratação: neutro, igual nos dois
  );

  if (variant === "user-desktop") {
    return (
      <>
        <header data-user-header="true" className={`sticky top-0 z-40 border-b border-[var(--fz-header-border)] bg-[var(--fz-header-bg)] ${paddingMobile} py-3.5 lg:hidden`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FotoPerfilUsuario
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl"
                style={{ background: "linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)" }}
              >
                {marca ? <span className="text-sm font-extrabold tracking-tight text-white">{marca.sigla}</span> : null}
              </FotoPerfilUsuario>
              <div className="leading-tight">
                {marca ? <p className="text-sm font-bold text-[var(--fz-header-text)]">{marca.nome}</p> : null}
                <p className="text-xs text-[var(--fz-header-text-muted)]">
                  <NomeUsuario />
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              {semElementosBp ? null : <GestorToggle />}
              {entradaPerfil}
            </div>
          </div>
        </header>

        <header className="hidden lg:block">
          <div
            data-user-desktop-topbar="true"
            data-user-header="true"
            className={`bg-[var(--fz-header-bg)] px-7 py-3 ${integrado ? "rounded-none border-b border-[var(--fz-header-border)] shadow-none" : "rounded-[20px] border border-[var(--fz-header-border)] shadow-[var(--shadow-soft)]"}`}
          >
            <div className="flex flex-row-reverse items-center justify-between gap-5">
              <div>
                <div className="flex items-center gap-4">
                  <div>
                    <div className="flex items-center gap-2.5">
                      <div className="flex items-baseline gap-2.5">
                        <p className="text-[17px] font-normal leading-none text-[var(--fz-header-text-muted)]">{saudacao},</p>
                        <h1 className="text-[24px] font-semibold leading-none text-[var(--fz-header-text)]">
                          <NomeUsuarioComPonto />
                        </h1>
                      </div>
                      {integrado && !semElementosBp ? <GestorToggle /> : null}
                      {integrado ? entradaPerfil : null}
                    </div>
                  </div>
                  <div className="flex h-9 items-center gap-1">
                    {badgesLiberados.map((badge) => (
                      <img
                        key={badge.src}
                        src={badge.src}
                        alt={badge.alt}
                        className="h-9 w-auto object-contain"
                      />
                    ))}
                  </div>
                </div>
              </div>

              {!integrado ? (
                <div className="flex items-center gap-2.5">
                  {semElementosBp ? null : <GestorToggle />}
                  {entradaPerfil}
                </div>
              ) : null}
            </div>
          </div>
        </header>
      </>
    );
  }

  return (
    <header
      data-user-desktop-topbar="true"
      className={`sticky top-0 z-40 border-b border-border bg-card ${paddingMobile} py-3.5 lg:rounded-[20px] lg:border-b lg:border-[var(--border)] lg:bg-[var(--surface-header)] lg:px-7 lg:py-3 lg:backdrop-blur`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 lg:gap-4">
          <FotoPerfilUsuario
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl lg:hidden"
            style={{ background: "linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)" }}
          >
            {marca ? <span className="text-sm font-extrabold tracking-tight text-white lg:text-base">{marca.sigla}</span> : null}
          </FotoPerfilUsuario>
          <div className="leading-tight">
            {marca ? <p className="text-sm font-bold text-[var(--text-primary)] lg:text-lg">{marca.nome}</p> : null}
            <p className="text-xs text-[var(--text-secondary)] lg:text-sm">
              <NomeUsuario />
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 lg:gap-3">
          {areaAnaliseGestor || followProUsuario ? null : <GestorToggle />}
          {entradaPerfil}
        </div>
      </div>
    </header>
  );
}
